import {
  Alert,
  AlertIcon,
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  Link,
  Select,
  Text,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { cloneDeep, uniqueId } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import * as Yup from "yup";
import { useNavigate, Link as RouterLink } from "react-router-dom";

import { USStates } from "src/common/USStates";
import { handleHookFormHTTPError } from "src/common/form";
import { HTTPError, useKy } from "src/common/ky";
import { Loading } from "src/common/Loading";
import { YupPossiblePhoneNumber } from "src/common/phone-numbers";
import { PhoneNumberInput } from "src/common/PhoneNumbers";
import {
  AddDealer as AddDealerType,
  DealerType,
  DEALER_TYPES,
} from "src/common/types";
import { PageContent } from "src/layout/PageContent";
import {
  PageHeader,
  PageHeaderBackLink,
  PageHeaderSubtitle,
  PageHeaderTitle,
} from "src/layout/PageHeader";

const AddDealerValidationSchema = Yup.object().shape({
  name: Yup.string().required().label("Name"),
  dba: Yup.string().optional().label("DBA"),
  phone: YupPossiblePhoneNumber({
    label: "Phone Number",
  }).required(),
  dealerType: Yup.string().oneOf(DEALER_TYPES).required().label("Dealer Type"),
  addressLine1: Yup.string().required().label("Address Line 1"),
  addressLine2: Yup.string().optional().default("").label("Address Line 2"),
  city: Yup.string().required().label("City"),
  state: Yup.string().required().label("State"),
  zipCode: Yup.string().required().label("Zip Code"),
  user: Yup.object()
    .required()
    .shape({
      firstName: Yup.string().required().label("First Name"),
      lastName: Yup.string().required().label("Last Name"),
      phone: YupPossiblePhoneNumber({ label: "Phone Number" }).required(),
      email: Yup.string().required().email().label("Email"),
    }),
  website: Yup.string()
    .matches(
      /((https?):\/\/)?(www.)?[a-z0-9-]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#-]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?/,
      "Please enter a valid url"
    )
    .required()
    .transform((v: string) =>
      /^((https?):\/\/).*$/i.test(v) ? v : `https://${v}`
    )
    .label("Website"),
  isDmpDealer: Yup.boolean().required().label("DMP Dealer"),
});

export const AddDealer = () => {
  const ky = useKy();
  const toast = useToast();
  const navigate = useNavigate();

  const responsiveWidths = ["100%", "100%", "50%", "50%"];

  const {
    control,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<AddDealerType>({
    defaultValues: {
      name: "",
      dba: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
      dealerType: DealerType.Billable,
      isDmpDealer: false,
      phone: "",
      website: "",
      user: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      },
    },
    resolver: yupResolver(AddDealerValidationSchema),
  });

  const onSubmit = handleSubmit((values) => {
    mutate(values);
  });

  const onSubmitSuccess = () => {
    toast({
      title: "Dealer created.",
      description: "The dealer organization was created successfully.",
      status: "success",
      isClosable: true,
    });

    navigate(`/dealers`);
  };

  const { isLoading, isSuccess, mutate } = useMutation<
    void,
    HTTPError,
    AddDealerType
  >(
    async (data) => {
      await ky.post("dealer-organizations", {
        json: cloneDeep(data),
      });

      reset();
    },
    {
      onSuccess: onSubmitSuccess,
      onError: handleHookFormHTTPError(setError, getValues, toast),
    }
  );

  if (isLoading && !isSuccess) {
    return <Loading />;
  }

  return (
    <>
      <PageHeader>
        <PageHeaderBackLink>
          <Link as={RouterLink} to="/dealers">
            &#8249; Back to list
          </Link>
        </PageHeaderBackLink>
        <PageHeaderTitle>Add Dealer Organization</PageHeaderTitle>
        <PageHeaderSubtitle>
          Enter the details for the new dealer here
        </PageHeaderSubtitle>
      </PageHeader>
      <PageContent>
        <form onSubmit={onSubmit}>
          <Grid gap={4}>
            <GridItem>
              <Heading as={"h3"} fontSize="24px" mt={4} mb={4}>
                Dealer Organization Details
              </Heading>
            </GridItem>

            <GridItem>
              <Divider />
            </GridItem>

            <GridItem>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="name">Name</FormLabel>
                    <Input
                      width={responsiveWidths}
                      {...field}
                      id="name"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="dba"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="dba">DBA (if applicable)</FormLabel>
                    <Input
                      width={responsiveWidths}
                      {...field}
                      id="dba"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="addressLine1"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="addressLine1">Address Line 1</FormLabel>
                    <Input
                      width={responsiveWidths}
                      {...field}
                      id="addressLine1"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="addressLine2"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="addressLine2">Address Line 2</FormLabel>
                    <Input
                      width={responsiveWidths}
                      {...field}
                      id="addressLine2"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="city"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="city">City</FormLabel>
                    <Input
                      width={responsiveWidths}
                      {...field}
                      id="city"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="state"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="state">State</FormLabel>
                    <Select
                      placeholder="- Select State -"
                      width={responsiveWidths}
                      {...field}
                      id="state"
                    >
                      {USStates.map((state) => (
                        <option
                          key={state.abbreviation}
                          value={state.abbreviation}
                        >
                          {state.name}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="zipCode"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="zipCode">Zip Code</FormLabel>
                    <Input
                      width={responsiveWidths}
                      {...field}
                      id="zipCode"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="dealerType"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="dealerType">Dealer Type</FormLabel>
                    <Select
                      width={responsiveWidths}
                      {...field}
                      id="dealerType"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    >
                      <option value="Internal">Internal</option>
                      <option value="Billable">Billable</option>
                    </Select>
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="isDmpDealer"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="isDmpDealer">DMP Dealer</FormLabel>
                    <Select
                      width={responsiveWidths}
                      {...field}
                      id="isDmpDealer"
                      onChange={(event) => {
                        field.onChange(event.target.value === "1");
                      }}
                      value={field.value ? "1" : "0"}
                    >
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </Select>
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="phone"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="phone">Phone</FormLabel>
                    <PhoneNumberInput
                      width={responsiveWidths}
                      {...field}
                      id="phone"
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="website"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="website">Website</FormLabel>
                    <Input
                      width={responsiveWidths}
                      {...field}
                      id="website"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Divider />
            </GridItem>

            <GridItem mt={12}>
              <Heading as={"h3"} fontSize="24px" mb={4}>
                Dealer Admin Contact Information
              </Heading>
              <Text color="gray.500" fontWeight="medium" fontSize="md">
                The Dealer Admin is the primary responsible party for the
                organization. This user will be able to change settings, add
                other users, and generally administer things from the property
                side.
              </Text>
            </GridItem>

            <GridItem>
              <Divider />
            </GridItem>

            <GridItem>
              <Controller
                name="user.firstName"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="user.firstName">First Name</FormLabel>
                    <Input
                      width={responsiveWidths}
                      {...field}
                      id="user.firstName"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="user.lastName"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="user.lastName">Last Name</FormLabel>
                    <Input
                      width={responsiveWidths}
                      {...field}
                      id="user.lastName"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="user.phone"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="user.phone">Phone Number</FormLabel>
                    <PhoneNumberInput
                      width={responsiveWidths}
                      {...field}
                      id="user.phone"
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="user.email"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="user.email">Email</FormLabel>
                    <Input
                      width={responsiveWidths}
                      {...field}
                      id="user.email"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            {errors &&
              Object.entries(errors).map(([field, error]) => {
                return error ? (
                  <GridItem key={uniqueId(field)}>
                    <Alert status="error">
                      <AlertIcon />
                      {error.message}
                    </Alert>
                  </GridItem>
                ) : null;
              })}

            <GridItem>
              <Divider />
            </GridItem>

            <GridItem>
              <ButtonGroup display="flex" justifyContent="flex-end">
                <Link as={RouterLink} to="/dealers">
                  <Button>Cancel</Button>
                </Link>
                <Button
                  type="submit"
                  colorScheme="brand.blue"
                  isLoading={isSubmitting}
                >
                  Save Dealer
                </Button>
              </ButtonGroup>
            </GridItem>
          </Grid>
        </form>
      </PageContent>
    </>
  );
};
